import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatStepper } from '@angular/material/stepper';
import { AbcCore } from '@barthauer/abc/core';
import {
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, concatMap, takeUntil, tap } from 'rxjs/operators';
import { IWizardExportPageComponent, WizardExportConfig } from './models/config.model';
import { WorkerResult, WorkerState } from 'libs/abc/core/data/src/lib/models/worker.model';
import { DataService } from 'libs/abc/core/data/src/lib/data.service';
import { WizardExportService } from './wizard-export.service';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';

@Component({
  selector: 'abc-wizard-export',
  templateUrl: './wizard-export.component.html',
  styleUrls: ['./wizard-export.component.scss'],
  standalone: false,
})
export class WizardExportComponent implements OnInit, OnDestroy {
  @ViewChild('stepper', { read: MatStepper, static: false }) stepper!: MatStepper;
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;
  public exportConfig: WizardExportConfig;
  public progressPercentage: number;
  public progressMessage: string;
  public retryButtonCaption: string;
  public processState: ProcessState;
  public ProcessState = ProcessState;
  public progressStatusFormat: (ratio: number, value: number) => string;
  private subscriptions = new Array<Subscription>();
  private exportSubject: Subject<void> = new Subject();
  private downloadSubject: Subject<void> = new Subject();
  private stopGettingCurrentStateSubject = new Subject<WorkerResult>();
  private cancelExportSubject: Subject<void> = new Subject();

  constructor(
    exportService: WizardExportService,
    private dataService: DataService,
    private resourceService: ResourceManagerService,
    private abcCore: AbcCore,
    private messageBoxService: MessageBoxService
  ) {
    this.resAsync = this.resourceService.getResourceSet('wizard-export');
    exportService.onLoadWizard().subscribe((config) => (this.exportConfig = config));

    this.updateProgress(0, ProcessState.BeforeFinishClick);

    this.progressStatusFormat = (ratio, value) => {
      const percent: number = ratio * 100;
      const percentText: string = percent.toFixed(0);
      let text: string = 'Fortschritt: ' + percentText + '%';
      if (this.progressMessage) {
        text += ' ' + this.progressMessage;
      }
      return text;
    };

    let currentWorkerResult: WorkerResult;
    this.subscriptions.push(
      this.exportSubject
        .asObservable()
        .pipe(
          concatMap(() => {
            this.updateProgress(0, ProcessState.Exporting, 'Starte Export...');
            return this.exportConfig?.startExportWorker(this.exportConfig.transferObject).pipe(
              concatMap((result) => {
                currentWorkerResult = result;
                return timer(0, 2000).pipe(
                  takeUntil(
                    this.stopGettingCurrentStateSubject.pipe(
                      concatMap((r) => {
                        if (r.state === WorkerState.CompletedSuccessfully) {
                          this.updateProgress(100, ProcessState.Done, 'Die Daten stehen auf dem Server bereit.');
                          return this.downloadFile(currentWorkerResult);
                        } else if (r.state === WorkerState.Cancelled) {
                          this.updateProgress(0, ProcessState.Cancelled, 'Der Vorgang wurde abgebrochen.');
                          return of(null);
                        } else {
                          return of(null);
                        }
                      })
                    )
                  ),
                  concatMap(() => {
                    this.updateProgressWorkerResult(result);
                    return dataService.getExtensionWorkersCurrentState(result).pipe(
                      tap((nextResult) => {
                        result = nextResult;
                        currentWorkerResult = nextResult;
                        this.updateProgressWorkerResult(result);
                        if (
                          result.state === WorkerState.CompletedSuccessfully ||
                          result.state === WorkerState.Cancelled ||
                          result.state === WorkerState.Faulted
                        ) {
                          this.stopGettingCurrentStateSubject.next(result);
                        }
                      }),
                      catchError((error) => {
                        this.stopGettingCurrentStateSubject.next(result);
                        return this.handleError(error);
                      })
                    );
                  }),
                  catchError((error) => this.handleError(error))
                );
              }),
              catchError((error) => this.handleError(error))
            );
          }),
          catchError((error) => this.handleError(error))
        )
        .subscribe(
          () => {},
          (error) => this.updateProgress(100, ProcessState.Error, error)
        )
    );

    this.subscriptions.push(
      this.downloadSubject
        .asObservable()
        .pipe(
          concatMap(() => this.downloadFile(currentWorkerResult).pipe(catchError((error) => this.handleError(error)))),
          catchError((error) => this.handleError(error))
        )
        .subscribe(
          () => {},
          (error) => this.updateProgress(100, ProcessState.Error, error)
        )
    );

    this.subscriptions.push(
      this.cancelExportSubject
        .asObservable()
        .pipe(
          concatMap(() => {
            this.stopGettingCurrentStateSubject.next(new WorkerResult({ state: WorkerState.Cancelled }));
            return this.dataService
              .cancelExtensionWorker(currentWorkerResult)
              .pipe(catchError((error) => this.handleError(error)));
          }),
          catchError((error) => this.handleError(error))
        )
        .subscribe(
          () => {},
          (error) => this.updateProgress(100, ProcessState.Error, error)
        )
    );
  }

  private handleError(error: any): Observable<Error> {
    this.updateProgress(100, ProcessState.Error, error);
    return of(error);
  }

  public stepperSelectionChange(stepper: MatStepper, event: StepperSelectionEvent) {
    if (event.selectedIndex !== 0) {
      //affects only content pages
      if (event.selectedIndex !== stepper.steps.length - 1) {
        event.selectedStep.completed = false;
      } else {
        //last/final page
        this.updateProgress(0, ProcessState.BeforeFinishClick);
      }
    }
  }
  private updateProgressWorkerResult(workerResult: WorkerResult) {
    if (workerResult.data) {
      this.updateProgress(workerResult.data.progress, ProcessState.Exporting, workerResult.data.message);
    }
  }
  private updateProgress(percentage: number, processState: ProcessState, textOrError?: any) {
    if (textOrError) {
      if (typeof textOrError === 'string' || textOrError instanceof String) {
        this.progressMessage = textOrError.toString();
      } else if (textOrError instanceof Error || textOrError instanceof HttpErrorResponse) {
        this.progressMessage = 'Fehler: ' + this.abcCore.error.getErrorInfo(textOrError).message;
      }
    } else {
      this.progressMessage = '';
    }

    //HACK: To update progress message when no progress percentage has changed
    if (this.progressPercentage !== undefined && this.progressPercentage === percentage) {
      this.progressPercentage += 0.001;
    } else {
      this.progressPercentage = percentage;
    }

    this.processState = processState;
    switch (this.processState) {
      case ProcessState.Cancelled:
        this.retryButtonCaption = 'Start';
        break;
      case ProcessState.Exporting:
        this.retryButtonCaption = 'Abbrechen';
        break;
      case ProcessState.Done:
        this.retryButtonCaption = 'Erneut herunterladen';
        break;
      case ProcessState.Error:
        this.retryButtonCaption = 'Erneut versuchen';
        break;
    }
  }

  private downloadFile(result: WorkerResult) {
    return this.dataService.getExtensionWorkersResult(result).pipe(
      tap((res) => {
        const fileReader = new FileReader();
        fileReader.readAsDataURL(res);

        fileReader.onload = () => {
          const downloadLink = document.createElement('a');
          downloadLink.setAttribute('download', this.getExportFileName());
          downloadLink.setAttribute('href', fileReader.result as string);
          downloadLink.click();
        };
      })
    );
  }

  private getExportFileName(): string {
    return this.exportConfig.exportFileNameSchema.replace(
      WizardExportConfig.TimeStamp_Placeholder,
      this.getTimeStamp()
    );
  }

  private getTimeStamp(): string {
    const currentDate = new Date();
    return (
      currentDate.getFullYear() +
      ('0' + (currentDate.getMonth() + 1)).slice(-2) +
      ('0' + currentDate.getDate()).slice(-2) +
      ('0' + currentDate.getHours()).slice(-2) +
      ('0' + currentDate.getMinutes()).slice(-2) +
      ('0' + currentDate.getSeconds()).slice(-2)
    );
  }

  async ngOnInit() {
    this.res = await this.resAsync;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  public moveStep(stepper: MatStepper, component: IWizardExportPageComponent): void {
    if (component.isValid) {
      component.onNext();
      stepper.selected.completed = true;
      stepper.next();
    } else if (component.invalidMessage) {
      this.showMessageBox(component.invalidMessage);
    }
  }

  public moveBack(): void {
    this.stepper.previous();
  }

  private showMessageBox(htmlMsg: string) {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.icon = MessageBoxIcon.Warning;
    settings.button = MessageBoxButtons.OK;
    settings.htmlcode = document.createElement('div');
    settings.htmlcode.innerHTML = htmlMsg;

    this.subscriptions.push(this.messageBoxService.showMessageBox(settings).subscribe());
  }

  public finishClick(): void {
    this.exportSubject.next();
  }

  public retryButtonClick(): void {
    switch (this.processState) {
      case ProcessState.Cancelled:
      case ProcessState.Error:
        this.exportSubject.next();
        break;
      case ProcessState.Exporting:
        this.cancelExportSubject.next();
        break;
      case ProcessState.Done:
        this.downloadSubject.next();
        break;
    }
  }
}

enum ProcessState {
  BeforeFinishClick = 1,
  Exporting,
  Cancelled,
  Done,
  Error,
}
